import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearStoredToken, getStoredToken } from '../utils/authToken';

// ─── Axios instance for health-check / evatools API ──────────────────────────

export const checkClient = axios.create({
  baseURL: '/api/v2',
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Request interceptor — attach Bearer token ──────────────────────────────

checkClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Let browser set Content-Type automatically for FormData (includes boundary)
    if (config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ─── Response interceptor — unwrap {code, data, message} → data ─────────────

checkClient.interceptors.response.use(
  (response) => {
    const payload = response.data;
    if (payload && typeof payload === 'object' && 'data' in payload) {
      return { ...response, data: payload.data };
    }
    return response;
  },
  (error: AxiosError<{ code?: number; message?: string }>) => {
    const msg =
      error.response?.data?.message ??
      error.message ??
      'Request failed, please try again';

    if (error.response?.status === 401) {
      clearStoredToken();
    }

    return Promise.reject(new Error(msg));
  },
);

// ─── Convenience wrappers ────────────────────────────────────────────────────

export async function checkGet<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const res = await checkClient.get<T>(url, { params });
  return res.data;
}

export async function checkPost<T>(
  url: string,
  data?: unknown,
): Promise<T> {
  const res = await checkClient.post<T>(url, data);
  return res.data;
}

// ─── Authenticated image fetch — returns a blob URL ──────────────────────────
// Use this instead of <img src> for auth-protected image endpoints.
// Caller is responsible for calling URL.revokeObjectURL(url) on cleanup.

export async function checkGetImageUrl(
  path: string,
  params?: Record<string, string>,
): Promise<string> {
  const token = getStoredToken();
  const url = new URL(`/api/v2${path}`, window.location.origin);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`image fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ─── Auth client (same base URL as checkClient) ──────────────────────────────
// Auth endpoints are at /api/v2/auth/*

export const authClient = axios.create({
  baseURL: '/api/v2',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

authClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredToken();
  if (token && config.headers) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

authClient.interceptors.response.use(
  (response) => {
    const payload = response.data;
    if (payload && typeof payload === 'object' && 'data' in payload) {
      return { ...response, data: payload.data };
    }
    return response;
  },
  (error: AxiosError<{ code?: number; message?: string }>) => {
    return Promise.reject(new Error(error.response?.data?.message ?? error.message ?? 'Auth request failed'));
  },
);

export default checkClient;
